import { useQuery } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';

export interface TimeSeriesPoint {
  date: string;
  value: number;
}

export interface DashboardMetrics {
  totalMembers: number;
  activeMembers: number;
  revenueThisMonth: number;
  goalsCompleted: number;
  memberGrowthTimeSeries: TimeSeriesPoint[];
  revenueTimeSeries: TimeSeriesPoint[];
}

export interface AnalyticsSummary {
  totalMembers: number;
  memberGrowthPercent: number;
  totalRevenue: number;
  revenueGrowthPercent: number;
  engagementRate: number;
  activePulsePosts: number;
  upcomingEventsCount: number;
  goalsCompleted?: number;
}

export const useAnalytics = (nodeId: string) => {
  return useQuery({
    queryKey: ['analytics', nodeId],
    queryFn: async () => {
      if (!nodeId || nodeId === 'platform-root') return null;
      const response = await apiWorker.get(`/api/nodes/${nodeId}/analytics`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!nodeId && nodeId !== 'platform-root'
  });
};

export const useAnalyticsSummary = (nodeId: string, isRollingUp = false) => {
  return useQuery<AnalyticsSummary>({
    queryKey: ['analytics-summary', nodeId, isRollingUp],
    queryFn: async () => {
      if (!nodeId || nodeId === 'platform-root') return null;
      const response = await apiWorker.get(`/api/nodes/${nodeId}/analytics/summary`, {
        params: { rollup: isRollingUp, includeSubnodes: isRollingUp }
      });
      if (response.data && Array.isArray(response.data.content)) {
        return response.data.content[0] || null;
      }
      return response.data;
    },
    enabled: !!nodeId && nodeId !== 'platform-root'
  });
};

export const useAnalyticsTimeSeries = (nodeId: string, range = '30d', metrics = 'members,revenue', isRollingUp = false) => {
  return useQuery<any>({
    queryKey: ['analytics-timeseries', nodeId, range, metrics, isRollingUp],
    queryFn: async () => {
      if (!nodeId || nodeId === 'platform-root') return null;
      const response = await apiWorker.get(`/api/nodes/${nodeId}/analytics/timeseries`, {
        params: { range, metric: metrics, rollup: isRollingUp, includeSubnodes: isRollingUp }
      });
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!nodeId && nodeId !== 'platform-root'
  });
};
